/**
 * Sort direction and the URL search change for a column-header click, shared
 * by every sortable admin table.
 *
 * Aliases: sort direction, asc desc, table sort, next sort search, sort toggle
 */

export const SORT_DIRECTIONS = ["asc", "desc"] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

/** How a page's sort columns start: which one is the default and each one's first direction. */
export interface SortDefaults<Sort extends string> {
  readonly sort: Sort;
  /** Names read best A-Z; dates and counts start newest or largest. */
  readonly direction: (sort: Sort) => SortDirection;
}

/**
 * The `sort` and `dir` URL params for a sort state. Whatever the page would
 * show anyway stays out of the URL, so the default view keeps a clean link.
 */
function sortSearch<Sort extends string>(
  state: { sort: Sort; direction: SortDirection },
  defaults: SortDefaults<Sort>,
): { sort: Sort | undefined; dir: SortDirection | undefined } {
  return {
    sort: state.sort === defaults.sort ? undefined : state.sort,
    dir:
      state.direction === defaults.direction(state.sort)
        ? undefined
        : state.direction,
  };
}

/**
 * The search change for a click on `column`'s header: the active column flips
 * direction, another column starts at its own default direction.
 */
export function nextSortSearch<Sort extends string>(
  current: { sort: Sort; direction: SortDirection },
  column: Sort,
  defaults: SortDefaults<Sort>,
): { sort: Sort | undefined; dir: SortDirection | undefined } {
  const direction: SortDirection =
    column === current.sort
      ? current.direction === "asc"
        ? "desc"
        : "asc"
      : defaults.direction(column);
  return sortSearch({ sort: column, direction }, defaults);
}
